import "./global.css";

import React, { Suspense, Component, ReactNode, ErrorInfo, useLayoutEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AnalyticsProvider } from "@/components/AnalyticsProvider";
import { ADMIN_SECRET_PATH } from "@/utils/admin-config";

// Global error boundary to catch dispatcher and hook errors
class GlobalErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error("[GlobalErrorBoundary] Error caught:", error.message);
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[GlobalErrorBoundary] Error details:", errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "100vh",
            backgroundColor: "#f9fafb",
            flexDirection: "column",
            fontFamily: "sans-serif",
          }}
        >
          <div style={{ maxWidth: "400px", textAlign: "center" }}>
            <h1 style={{ color: "#dc2626", marginBottom: "1rem" }}>
              Application Error
            </h1>
            <p style={{ color: "#4b5563", marginBottom: "2rem" }}>
              The application encountered an unexpected error. Please refresh
              the page to continue.
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: "#2563eb",
                color: "white",
                padding: "0.75rem 1.5rem",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
                fontSize: "1rem",
                fontWeight: "500",
              }}
            >
              Refresh Page
            </button>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <pre
                style={{
                  marginTop: "2rem",
                  backgroundColor: "#fee2e2",
                  padding: "1rem",
                  borderRadius: "0.375rem",
                  fontSize: "0.75rem",
                  color: "#991b1b",
                  textAlign: "left",
                  overflow: "auto",
                }}
              >
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Eagerly load homepage and redirect pages
import Ride from "./pages/Ride";
import NotFound from "./pages/NotFound";
import Join from "./pages/Join";
import DriverPortal from "./pages/DriverPortal";

// Lazy load pages
const AirportDetail = React.lazy(() => import("./pages/AirportDetail"));
const CityDetail = React.lazy(() => import("./pages/CityDetail"));
const Cities = React.lazy(() => import("./pages/Cities"));
const TownDetail = React.lazy(() => import("./pages/TownDetail"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const Help = React.lazy(() => import("./pages/Help"));
const Drive = React.lazy(() => import("./pages/Drive"));
const Driver = React.lazy(() => import("./pages/Driver"));
const Reserve = React.lazy(() => import("./pages/Reserve"));
const PopularRoutes = React.lazy(() => import("./pages/PopularRoutes"));
const Airports = React.lazy(() => import("./pages/Airports"));
const AppDownload = React.lazy(() => import("./pages/AppDownload"));
const AllLocationsDirectory = React.lazy(
  () => import("./pages/AllLocationsDirectory"),
);
const TestLocation = React.lazy(() => import("./pages/TestLocation"));
const Coverage = React.lazy(() => import("./pages/Coverage"));
const Business = React.lazy(() => import("./pages/Business"));
const Careers = React.lazy(() => import("./pages/Careers"));
const Press = React.lazy(() => import("./pages/Press"));
const Blog = React.lazy(() => import("./pages/Blog"));
const Safety = React.lazy(() => import("./pages/Safety"));
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const Contact = React.lazy(() => import("./pages/Contact"));
const WhatsApp = React.lazy(() => import("./pages/WhatsApp"));
const LocationAnalytics = React.lazy(() => import("./pages/LocationAnalytics"));
const Admin = React.lazy(() => import("./pages/Admin"));
const AdminProtected = React.lazy(() => import("./components/AdminProtected"));
const AnalyticsDashboard = React.lazy(
  () => import("./pages/AnalyticsDashboard"),
);
const EnhancedAnalyticsDashboard = React.lazy(
  () => import("./pages/EnhancedAnalyticsDashboard"),
);
const CustomizableDashboard = React.lazy(
  () => import("./pages/CustomizableDashboard"),
);
const PricingManagement = React.lazy(() => import("./pages/PricingManagement"));
const RobotsTest = React.lazy(() => import("./pages/RobotsTest"));
const SiteMap = React.lazy(() => import("./pages/SiteMap"));
const Htm = React.lazy(() => import("./pages/Htm"));
const AirportTransferPages = React.lazy(
  () => import("./pages/AirportTransferPages"),
);
const WeddingTransport = React.lazy(() => import("./pages/WeddingTransport"));
const GroupTravel = React.lazy(() => import("./pages/GroupTravel"));
const BookOnline = React.lazy(() => import("./pages/BookOnline"));
const RiderApp = React.lazy(() => import("./pages/RiderApp"));
const DriverCom = React.lazy(() => import("./pages/DriverCom"));
const TermsAndConditions = React.lazy(
  () => import("./pages/TermsAndConditions"),
);

const queryClient = new QueryClient();

const LegacyCityRedirect = () => {
  const { cityName = "" } = useParams<{ cityName?: string }>();
  return <Navigate to={cityName ? `/taxi/${cityName}` : "/taxi"} replace />;
};

const WHATSAPP_JOIN_URL =
  "https://wa.me/441162168222?text=Hi%2C%20I%20would%20Like%20to%20Join%20Tigola";

const WhatsAppJoinRedirect = () => {
  useLayoutEffect(() => {
    window.location.replace(WHATSAPP_JOIN_URL);
  }, []);

  return null;
};

const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600">Loading...</p>
    </div>
  </div>
);

const AppContent = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AnalyticsProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Homepage */}
              <Route path="/" element={<Ride />} />

              {/* Airport Routes */}
              <Route path="/airport/:airportSlug" element={<AirportDetail />} />
              <Route
                path="/airports/:airportCode"
                element={<AirportDetail />}
              />
              <Route path="/airports" element={<Airports />} />
              <Route
                path="/airport-transfers/:route"
                element={<AirportTransferPages />}
              />

              {/* City Routes - Canonical paths */}
              <Route path="/taxi/:cityName" element={<CityDetail />} />
              <Route path="/taxi" element={<Cities />} />

              {/* Town routes */}
              <Route path="/taxis/:townName" element={<TownDetail />} />

              {/* Services */}
              <Route path="/ride" element={<Ride />} />
              <Route path="/drive" element={<Drive />} />
              <Route path="/driver" element={<Driver />} />
              <Route path="/driverportal" element={<DriverPortal />} />
              <Route path="/join" element={<Join />} />
              <Route path="/driverapp" element={<WhatsAppJoinRedirect />} />
              <Route path="/business" element={<Business />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/help" element={<Help />} />
              <Route path="/wedding-transport" element={<WeddingTransport />} />

              {/* Travel */}
              <Route path="/reserve" element={<Reserve />} />
              <Route path="/popular-routes" element={<PopularRoutes />} />
              <Route path="/group-travel" element={<GroupTravel />} />

              {/* App */}
              <Route path="/app" element={<AppDownload />} />
              <Route path="/book-online" element={<BookOnline />} />
              <Route path="/riderapp" element={<RiderApp />} />
              <Route path="/drivercom" element={<DriverCom />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsAndConditions />}
              />

              {/* Company & Info */}
              <Route path="/careers" element={<Careers />} />
              <Route path="/press" element={<Press />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/safety" element={<Safety />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/whatsapp" element={<WhatsApp />} />

              {/* Coverage & Locations */}
              <Route path="/coverage" element={<Coverage />} />
              <Route path="/locations" element={<AllLocationsDirectory />} />

              {/* Test/Tools */}
              <Route path="/test-location" element={<TestLocation />} />
              <Route path="/test-robots" element={<RobotsTest />} />
              <Route path="/sitemap" element={<SiteMap />} />
              <Route path="/htm" element={<Htm />} />

              {/* Admin (public entry redirects) */}
              <Route path="/admin" element={<Navigate to="/" replace />} />
              {/* Secret admin paths */}
              <Route path={ADMIN_SECRET_PATH} element={<Admin />} />
              <Route
                path={`${ADMIN_SECRET_PATH}/analytics`}
                element={<EnhancedAnalyticsDashboard />}
              />
              <Route
                path="/dashboard"
                element={<EnhancedAnalyticsDashboard />}
              />
              <Route
                path={`${ADMIN_SECRET_PATH}/dashboard`}
                element={<AnalyticsDashboard />}
              />
              <Route
                path={`${ADMIN_SECRET_PATH}/analytics/custom`}
                element={<CustomizableDashboard />}
              />
              <Route
                path={`${ADMIN_SECRET_PATH}/analytics/basic`}
                element={<AnalyticsDashboard />}
              />
              <Route
                path={`${ADMIN_SECRET_PATH}/pricing`}
                element={<PricingManagement />}
              />
              <Route
                path="/analytics"
                element={
                  <Navigate to={`${ADMIN_SECRET_PATH}/analytics`} replace />
                }
              />
              <Route
                path="/analytics/basic"
                element={
                  <Navigate to={`${ADMIN_SECRET_PATH}/analytics`} replace />
                }
              />
              <Route
                path="/analytics/custom"
                element={
                  <Navigate to={`${ADMIN_SECRET_PATH}/analytics`} replace />
                }
              />

              {/* Main Location Landing Pages - Redirects to canonical URLs */}
              <Route
                path="/leicester"
                element={<Navigate to="/taxi/leicester" replace />}
              />
              <Route
                path="/birmingham"
                element={<Navigate to="/taxi/birmingham" replace />}
              />
              <Route
                path="/birmingham-city"
                element={<Navigate to="/taxi/birmingham" replace />}
              />

              {/* Deprecated service routes - redirect to canonical URLs */}
              <Route
                path="/executive"
                element={<Navigate to="/business" replace />}
              />
              <Route
                path="/corporate-travel"
                element={<Navigate to="/business" replace />}
              />
              <Route
                path="/group-travel"
                element={<Navigate to="/business" replace />}
              />

              {/* Legacy cities routes - redirect to canonical /taxi/ paths */}
              <Route path="/cities/:cityName" element={<LegacyCityRedirect />} />
              <Route path="/cities" element={<Navigate to="/taxi" replace />} />

              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AnalyticsProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

const App = () => (
  <GlobalErrorBoundary>
    <AppContent />
  </GlobalErrorBoundary>
);

// Only create and render root once to prevent HMR issues
declare global {
  interface Window {
    __TIGOLA_ROOT__?: ReturnType<typeof createRoot>;
  }
}

const container = document.getElementById("root");

if (!container) {
  throw new Error("Root container not found");
}

// Reuse existing root on HMR updates, or create new one
const root = window.__TIGOLA_ROOT__ || createRoot(container);
window.__TIGOLA_ROOT__ = root;
root.render(<App />);

// Register PWA service worker
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/service-worker.js", { scope: "/" })
      .then((registration) => {
        // If an update is found, activate it as soon as possible
        if (registration.waiting) {
          registration.waiting.postMessage({ type: "SKIP_WAITING" });
        }
        registration.addEventListener("updatefound", () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener("statechange", () => {
            if (
              newWorker.state === "installed" &&
              navigator.serviceWorker.controller
            ) {
              // New content is available; next navigation will be controlled
              newWorker.postMessage({ type: "SKIP_WAITING" });
            }
          });
        });
      })
      .catch((err) => {
        console.warn("Service worker registration failed:", err);
      });
  });
}
